import { memo, useEffect, useMemo } from "react";

import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";

import InfiniteScrollWrapper from "../../../../../../common/components/infinite-scroll/InfiniteScrollWrapper";
import FullPageLoader from "../../../../../../common/components/item/FullPageLoader";
import CircularLoading from "../../../../../../common/components/progress/CircularLoading";
import TableNoDataOverlay from "../../../../../../common/components/table/TableNoDataOverlay";
import useWindowDimensions from "../../../../../../common/hooks/useWindowDimensions";
import { AutocompleteOptionType } from "../../../../../../common/models/autocomplete";

import { useSelectedDataHubTagLocations } from "../../../dataHub.variable";
import { useGetDevicesByLocationOrTag } from "../../hooks/useGetDevicesByLocationOrTag";
import { useSelectedLocationDropdown } from "../../../../../../common/variables/selectedLocationDropdown";
import { useSearchParams } from "react-router-dom";
import DeviceImageListItem from "./DeviceImageListItem";

interface IDevicesImageListProps {
  targetDeviceName?: string | null;
}

const DevicesImageList = ({
  targetDeviceName,
}: IDevicesImageListProps): JSX.Element => {
  const theme = useTheme();
  const { width } = useWindowDimensions();

  const tagLocationVariable = useSelectedDataHubTagLocations();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedLocation = useSelectedLocationDropdown();

  const { devices, loading, loadMoreDevices, nextToken, fetchMoreLoading } =
    useGetDevicesByLocationOrTag();

  const memoizedCards = useMemo(() => {
    let cards = [...devices];

    if (tagLocationVariable?.length) {
      cards = devices?.filter(item => {
        const nameOfLocation = item.locationId;

        const locations = tagLocationVariable.map(
          (tag: AutocompleteOptionType) => {
            if (nameOfLocation.includes("#L#")) {
              return tag?.value?.toLowerCase() ?? "";
            }

            return tag?.title?.toLowerCase() ?? "";
          }
        );

        const condition = locations.includes(nameOfLocation.toLowerCase());

        return condition;
      });
    }

    return cards;
  }, [devices, tagLocationVariable]);

  // Auto-fetch pages to find target device, or clean up invalid device param
  useEffect(() => {
    if (!memoizedCards?.length && loading && !fetchMoreLoading) return;

    const deviceParam = searchParams.get("device");

    if (!deviceParam) return;

    // Don't clean up until the correct location's devices are loaded
    const locationParam = searchParams.get("location");

    if (
      locationParam &&
      selectedLocation?.title?.toLowerCase() !== locationParam.toLowerCase()
    ) {
      return;
    }

    // Wait for devices to finish loading before checking
    if (loading) return;

    const deviceFound = memoizedCards.find(
      card => card.name.toLowerCase() === deviceParam.toLowerCase()
    );

    if (deviceFound) return;

    // Don't clean up if we have no cards to check against (transitional state)
    if (!memoizedCards.length) return;

    // Verify devices belong to the correct location (guards against stale data)
    if (locationParam) {
      const locationSlug = locationParam.toLowerCase().replace(/\s+/g, "_");
      const firstDeviceLocation =
        memoizedCards[0]?.locationId?.toLowerCase() ?? "";

      if (!firstDeviceLocation.includes(locationSlug)) return;
    }

    if (nextToken && !loading && !fetchMoreLoading) {
      loadMoreDevices();
    } else if (!nextToken) {
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);

          newParams.delete("device");

          return newParams;
        },
        { replace: true }
      );
    }
  }, [
    memoizedCards,
    loading,
    fetchMoreLoading,
    nextToken,
    selectedLocation?.title,
  ]);

  const cardWidth = width < theme.breakpoints.values.xxl ? "294px" : "386px";

  if (loading && !fetchMoreLoading) {
    return <FullPageLoader />;
  }

  if (!memoizedCards?.length && !loading && !fetchMoreLoading) {
    return <TableNoDataOverlay />;
  }

  return (
    <Box
      sx={{
        display: "grid",
        gap: "1.25em",
        gridTemplateColumns: `repeat(auto-fill, minmax(min(${cardWidth}, 100%), 1fr))`,
      }}
      component="div"
    >
      <InfiniteScrollWrapper
        fetchNextPage={loadMoreDevices}
        lastEvaluatedKey={nextToken}
        loading={loading || fetchMoreLoading}
      >
        {memoizedCards &&
          memoizedCards.map((item): JSX.Element => {
            const shouldAutoOpen = !!(
              targetDeviceName &&
              item.name.toLowerCase() === targetDeviceName.toLowerCase()
            );

            return (
              <DeviceImageListItem
                key={item.id}
                device={item}
                autoOpen={shouldAutoOpen}
              />
            );
          })}
        {fetchMoreLoading && <CircularLoading />}
      </InfiniteScrollWrapper>
    </Box>
  );
};

export default memo(DevicesImageList);
